import { useQuery } from '@tanstack/react-query';

import axios from '@/react/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { AuthorizationMap } from '@/react/portainer/users/RolesView/types';
import { isAxiosError } from '@/portainer/services/axios/utils/isAxiosError';

import { User } from '../types';

import { userQueryKeys } from './queryKeys';

export interface CurrentUserResponse extends User {
  forceChangePassword?: boolean;
  PortainerAccessDenied?: boolean;
}

export function useLoadCurrentUser({ staleTime }: { staleTime?: number } = {}) {
  return useQuery(userQueryKeys.me(), () => getCurrentUser(), {
    ...withError('Unable to retrieve user details'),
    staleTime,
    retry(failureCount: number, error: unknown) {
      return (
        failureCount < 3 &&
        (!isAxiosError(error) || error.response?.status !== 401)
      );
    },
  });
}

export async function getCurrentUser() {
  const { data: user } = await axios.get<CurrentUserResponse>('/users/me', {
    params: { noEndpointAuthorizations: true },
  });

  return user;
}

export function useUserEnvironmentAuthorizations({
  environmentId,
  enabled,
}: {
  environmentId?: EnvironmentId;
  enabled?: boolean;
}) {
  return useQuery(
    userQueryKeys.authorizations(environmentId!),
    () => getUserEnvironmentAuthorizations(environmentId!),
    {
      enabled: !!environmentId && !!enabled,
      ...withError(
        `Unable to retrieve user authorizations for environment ${environmentId}`
      ),
    }
  );
}

type EnvironmentAuthorizations = {
  Authorizations: AuthorizationMap;
};

export async function getUserEnvironmentAuthorizations(
  environmentId?: EnvironmentId
) {
  if (!environmentId) {
    return {};
  }

  const { data } = await axios.get<EnvironmentAuthorizations>(
    `/users/me/auth/${environmentId}`
  );

  return { [environmentId]: data.Authorizations };
}

type NamespaceAuthorizationsResponse = {
  namespaceAuthorizations: Record<string, AuthorizationMap>;
};

export function useUserKubernetesNamespaceAuthorizations({
  environmentId,
  enabled,
}: {
  environmentId?: EnvironmentId;
  enabled?: boolean;
}) {
  return useQuery(
    userQueryKeys.namespaceAuthorizations(environmentId!),
    () => getUserKubernetesNamespaceAuthorizations(environmentId!),
    {
      enabled: !!environmentId && !!enabled,
      ...withError(
        `Unable to retrieve per-namespace Kubernetes authorizations for environment ${environmentId}`
      ),
    }
  );
}

export async function getUserKubernetesNamespaceAuthorizations(
  environmentId: EnvironmentId
): Promise<Record<string, AuthorizationMap>> {
  const { data } = await axios.get<NamespaceAuthorizationsResponse>(
    `/users/me/auth/${environmentId}/namespaces`
  );
  return data.namespaceAuthorizations ?? {};
}
